import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, RouterLink } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faShuffle, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { RecipeService, RecipeListItem } from '../../services/recipe/recipe.service';
import { RecipeFacetBadgesComponent } from '../recipe-facets/recipe-facet-badges.component';
import { UiStarBarComponent } from '../../shared/ui/star-bar/ui-star-bar.component';
import { AppLogger, AppLoggerService } from '../../core/logging';

/**
 * Home dashboard "Cook Now" widget — mirrors the iOS SeasonalRecipesView: a
 * horizontal carousel of random, in-season main dishes. The shuffle button asks
 * the server for a fresh random set. Tapping a card opens the recipe.
 */
@Component({
  selector: 'app-recipe-suggestions-teaser',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    RouterLink,
    FontAwesomeModule,
    RecipeFacetBadgesComponent,
    UiStarBarComponent,
  ],
  templateUrl: './recipe-suggestions-teaser.component.html',
  styleUrls: ['./recipe-suggestions-teaser.component.scss'],
})
export class RecipeSuggestionsTeaserComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  private readonly log: AppLogger;

  @Input() householdId?: number;
  /** Upper bound of suggestions to fetch; how many actually render adapts to width. */
  @Input() limit = 4;
  /** Bumped by the parent on context change to force a reload. */
  @Input() reloadToken?: number;

  recipes: RecipeListItem[] = [];
  /**
   * Signal so the parent's `[disabled]="suggestions.loading()"` binding stays
   * reconciled when load() flips it during the same change-detection cycle
   * (avoids ExpressionChangedAfterItHasBeenCheckedError).
   */
  readonly loading = signal(false);

  /** Card width + gap from the SCSS (200px card, 0.75rem gap). */
  private static readonly CARD_STRIDE = 212;
  /** Number of cards to render; adapts to the widget's width (see updateVisibleCount). */
  visibleCount = 0;
  private lastWidth = 0;
  private resizeObserver?: ResizeObserver;

  protected readonly faShuffle = faShuffle;
  protected readonly faUtensils = faUtensils;

  constructor(
    private recipeService: RecipeService,
    private appLogger: AppLoggerService,
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {
    this.log = this.appLogger.createForClass(RecipeSuggestionsTeaserComponent);
  }

  /**
   * Cards actually shown. Before the first measurement (visibleCount === 0) we
   * render everything so the widget is never empty; the ResizeObserver then
   * trims it to fit.
   */
  get displayedRecipes(): RecipeListItem[] {
    return this.visibleCount > 0 ? this.recipes.slice(0, this.visibleCount) : this.recipes;
  }

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0;
      // ResizeObserver fires outside Angular; re-enter so the view updates.
      this.zone.run(() => this.updateVisibleCount(width));
    });
    this.resizeObserver.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /**
   * Show as many cards as fit the current width plus one peeking past the edge
   * as a scroll cue — but never more than were fetched. Narrower widgets thus
   * display fewer recipes instead of forcing a long off-screen row.
   */
  private updateVisibleCount(width: number): void {
    this.lastWidth = width;
    if (width <= 0 || this.recipes.length === 0) return;
    const fits = Math.max(1, Math.floor((width + 12) / RecipeSuggestionsTeaserComponent.CARD_STRIDE));
    this.visibleCount = Math.min(this.recipes.length, fits + 1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['householdId'] || changes['limit'] || changes['reloadToken']) {
      this.load();
    }
  }

  /** Fetch a fresh random set (also the shuffle action). */
  reroll(): void {
    this.load();
  }

  private load(): void {
    const hid = this.householdId;

    // Context switches briefly leave householdId null; don't hit the API then.
    if (hid == null) {
      this.recipes = [];
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    // season omitted → the server computes the current season.
    this.recipeService.getRecipeSuggestions(hid, undefined, this.limit).subscribe({
      next: (recipes) => {
        this.recipes = recipes ?? [];
        // Re-fit against the last measured width now that the set changed.
        this.updateVisibleCount(this.lastWidth);
      },
      error: (err) => {
        this.log.error('Failed to load recipe suggestions', err, { householdId: hid });
        this.recipes = [];
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  trackById(_index: number, item: RecipeListItem): number {
    return item.id;
  }

  totalMinutes(recipe: RecipeListItem): number {
    return recipe.total_time_minutes ?? 0;
  }
}
